import { Component, inject, input, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { AppStateService } from "@services/index";

/**
 * No-reload data refresh button, shown on every page's header/toolbar next
 * to its "Add X" button. Re-validates the session (via
 * `AppStateService.refreshCurrentView`) before invoking the page's own
 * existing data-loading method — on an expired/missing session it hard
 * reloads to `/login` instead, exactly like the interceptor's expired-session
 * handling.
 *
 * Usage: `<fh-refresh-button [refresh]="reloadData" />` where `reloadData`
 * is a bound (arrow-function) reference to the page's own refetch method.
 */
@Component({
  selector: "fh-refresh-button",
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: "./refresh-button.component.html",
  styleUrls: ["./refresh-button.component.scss"],
})
export class RefreshButtonComponent {
  /** The page's own data-loading method, e.g. `loadVendors`. */
  refresh = input.required<() => void>();

  private readonly appStateService = inject(AppStateService);
  readonly isRefreshing = signal(false);

  onClick(): void {
    if (this.isRefreshing()) return;
    this.isRefreshing.set(true);
    this.appStateService.refreshCurrentView(() => {
      try {
        this.refresh()();
      } finally {
        this.isRefreshing.set(false);
      }
    });
  }
}
